import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";
import { LuCheck, LuX } from "react-icons/lu";
import ResponsiveImage, { type ResponsiveImageProps } from "@/components/ui/responsive-image.tsx";
import { cn } from "@/utils/style.ts";
import { DiagnosticsCard } from "./diagnostics-card.tsx";

type PartStatusItemVariants = VariantProps<typeof partStatusVariants>;
type PartStatusItemProps = ComponentPropsWithRef<"div"> & PartStatusItemVariants;

const partStatusVariants = cva("part-status-item", {
    variants: {
        status: {
            unavailable: "part-status-item--unavailable",
        },
    },
});

export function PartStatusItem({ className, status, ...props }: PartStatusItemProps) {
    return (
        <DiagnosticsCard variant={status === "unavailable" ? "error" : "success"}>
            <div {...props} className={partStatusVariants({ status, className })} />
        </DiagnosticsCard>
    );
}

type PartStatusImageWrapperProps = ComponentPropsWithRef<"div">;
export function PartStatusImageWrapper({ className, ...props }: PartStatusImageWrapperProps) {
    return <div {...props} className={cn("part-status-item__image-wrapper", className)} />;
}

type PartStatusImageProps = Omit<ResponsiveImageProps, "sizes">;
export function PartStatusImage({ className, ...props }: PartStatusImageProps) {
    return <ResponsiveImage {...props} className={cn("part-status-item__image", className)} sizes="120px" />;
}

type PartStatusImagePlaceholderProps = ComponentPropsWithRef<"div">;
export function PartStatusImagePlaceholder({ className, ...props }: PartStatusImagePlaceholderProps) {
    return <div {...props} className={cn("part-status-item__image-placeholder", className)} />;
}

type PartStatusBodyProps = ComponentPropsWithRef<"div">;
export function PartStatusBody({ className, ...props }: PartStatusBodyProps) {
    return <div {...props} className={cn("part-status-item__body", className)} />;
}

type PartStatusNameProps = ComponentPropsWithRef<"span">;
export function PartStatusName({ className, ...props }: PartStatusNameProps) {
    return <span {...props} className={cn("part-status-item__name", className)} />;
}

type PartStatusPartNoProps = ComponentPropsWithRef<"code">;
export function PartStatusPartNo({ className, ...props }: PartStatusPartNoProps) {
    return <code {...props} className={cn("part-status-item__part-no", className)} />;
}

const badgeVariants = cva("part-status-badge", {
    variants: {
        available: {
            true: "part-status-badge--available",
            false: "part-status-badge--unavailable",
        },
    },
});

type PartStatusBadgeProps = { available: boolean; className?: string };
export function PartStatusBadge({ available, className }: PartStatusBadgeProps) {
    const Icon = available ? LuCheck : LuX;
    const label = available ? "Available" : "Missing";

    return (
        <span className={badgeVariants({ available, className })}>
            <Icon className="part-status-badge__icon" />
            {label}
        </span>
    );
}
